import { FC, useCallback, useEffect, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import { useSelector } from 'react-redux';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

import useToggle from '@hooks/useToggle';
import { handleGetAdminIngredientsSearch } from '@store/features/adminInquiries/ingredientsSearch/actions';
import {
  adminIngredientsSearchIsLoadingSelector,
  adminIngredientsSearchPaginationSelector,
  adminIngredientsSearchSelector,
} from '@store/features/adminInquiries/ingredientsSearch/selectors';
import { removeAdminIngredientsSearch } from '@store/features/adminInquiries/ingredientsSearch/slice';
import useDispatch from '@store/hooks/useDispatch';

import { IIngredientsSearchSelectBoxProps } from './IngredientsSearchSelectBox.interface';
import { isOptionEqualToValue } from './utils';

import './style.scss';

const IngredientsSearchSelectBox: FC<IIngredientsSearchSelectBoxProps> = ({
  label,
  input: { onChange, value, ...otherInput },
  ...other
}) => {
  const dispatch = useDispatch();

  const adminIngredientsSearch = useSelector(adminIngredientsSearchSelector);
  const adminIngredientsSearchPagination = useSelector(adminIngredientsSearchPaginationSelector);
  const adminIngredientsSearchIsLoading = useSelector(adminIngredientsSearchIsLoadingSelector);

  const [listboxNode, setListboxNode] = useState<(EventTarget & Element) | null>(null);
  const [listboxNodeScrollTop, setListboxNodeScrollTop] = useState(0);
  const [isOpen, handleOpen, handleClose] = useToggle();

  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    listboxNode && (listboxNode.scrollTop = listboxNodeScrollTop);
  }, [adminIngredientsSearch]);

  const handleSearch = useCallback(
    (value: string) => {
      dispatch(removeAdminIngredientsSearch());
      setSearchValue(value.trim());
    },
    [dispatch],
  );

  const searchIngredients = useCallback(
    (searchValue: string) => {
      const page = adminIngredientsSearchPagination?.page || 1;
      const pageCount = adminIngredientsSearchPagination?.pageCount || 0;

      if ((page === pageCount && pageCount > 1) || searchValue.length < 3) {
        return;
      }

      dispatch(
        handleGetAdminIngredientsSearch({
          search: searchValue,
          page: page < pageCount ? page + 1 : page,
        }),
      );
    },
    [adminIngredientsSearchPagination?.page, adminIngredientsSearchPagination?.pageCount, dispatch],
  );

  useEffect(() => {
    searchIngredients(searchValue);
  }, [searchIngredients, searchValue]);

  const handleSelect = useCallback(
    (event: any, val: any) => {
      onChange(val);
    },
    [onChange],
  );

  return (
    <Box className="ingredients-search-select-box">
      <Autocomplete
        {...other}
        multiple
        clearOnBlur={false}
        id="ingredients-search-select-box"
        disablePortal
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        options={adminIngredientsSearch}
        getOptionLabel={option => option.name}
        value={value}
        filterSelectedOptions
        loading={adminIngredientsSearchIsLoading}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={() => adminIngredientsSearch}
        onChange={handleSelect}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <span className="ingredients-search-select-box__title">{option.name}</span>
          </li>
        )}
        renderInput={params => (
          <DebounceInput
            {...params}
            label={label}
            variant="filled"
            className="ingredients-search-select-box__input"
            debounceTimeout={400}
            element={TextField}
            minLength={3}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              handleSearch(event.target.value)
            }
            value={searchValue}
            InputProps={{
              ...otherInput,
              ...params.InputProps,
              endAdornment: (
                <>
                  {adminIngredientsSearchIsLoading && (
                    <CircularProgress color="primary" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            setListboxNode(event.currentTarget);

            const scrollTop = Math.ceil(listboxNode?.scrollTop || 0);
            setListboxNodeScrollTop(scrollTop);

            if (
              !adminIngredientsSearchIsLoading &&
              listboxNode &&
              scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight
            ) {
              searchIngredients(searchValue);
            }
          },
        }}
      />
    </Box>
  );
};

export default IngredientsSearchSelectBox;
